import { GpxRouteApi } from './GpxRouteApi';
import { escapeHtml, escapeAttribute } from './MapGpxUtils';

/** The three element ids a tag editor binds to. */
export interface IGpxTagEditorElements {
    /** Text input the user types a tag into. */
    inputId: string;
    /** Container the removable tag chips are rendered into. */
    chipsId: string;
    /** <datalist> populated with the user's existing tags for autocomplete. */
    suggestionsId: string;
}

/**
 * Reusable tag chip editor: commits a tag on Enter/comma/datalist-selection,
 * renders removable chips, and offers autocomplete from the user's own existing
 * tags. Shared by the route details dialog and the GPX upload modal so the two
 * can't drift.
 */
export class GpxTagEditor {

    private tags: string[] = [];

    public constructor(
        private api: GpxRouteApi,
        private elements: IGpxTagEditorElements
    ) {
        const input = this.input;
        if (!input) {
            return;
        }

        input.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ',') {
                event.preventDefault();
                this.commitPending();
            }
        });
        //a datalist selection fires change — commit it as a tag
        input.addEventListener('change', () => this.commitPending());
    }

    /** Reloads the editor with the given tags and refreshes the suggestion list. */
    public reset(tags: string[] | null | undefined): void {
        this.tags = [...(tags ?? [])];
        if (this.input) {
            this.input.value = '';
        }
        this.renderChips();
        void this.loadSuggestions();
    }

    /** Commits any half-typed tag, then returns the current set. */
    public getTags(): string[] {
        this.commitPending();
        return [...this.tags];
    }

    /** Moves whatever is in the input into the chip list (deduped, lower-cased). */
    public commitPending(): void {
        const input = this.input;
        if (!input) {
            return;
        }
        const value = input.value.trim().toLowerCase();
        if (value && this.tags.indexOf(value) < 0) {
            this.tags.push(value);
            this.renderChips();
        }
        input.value = '';
    }

    private get input(): HTMLInputElement | null {
        return <HTMLInputElement>document.getElementById(this.elements.inputId);
    }

    private renderChips(): void {
        const container = document.getElementById(this.elements.chipsId);
        if (!container) {
            return;
        }

        container.innerHTML = this.tags
            .map(t => `<span class="badge rounded-pill bg-primary me-1 mb-1" data-tag="${escapeAttribute(t)}">${escapeHtml(t)} <i class="fas fa-times" role="button" aria-label="Remove ${escapeAttribute(t)}"></i></span>`)
            .join('');

        container.querySelectorAll('i').forEach(icon => {
            icon.addEventListener('click', () => {
                const tag = icon.parentElement.getAttribute('data-tag');
                this.tags = this.tags.filter(t => t !== tag);
                this.renderChips();
            });
        });
    }

    /** Autocomplete comes from the user's own existing tags. */
    private async loadSuggestions(): Promise<void> {
        const datalist = document.getElementById(this.elements.suggestionsId);
        if (!datalist) {
            return;
        }
        try {
            const summary = await this.api.getTagSummary();
            datalist.innerHTML = summary
                .map(t => `<option value="${escapeAttribute(t.tag)}"></option>`)
                .join('');
        } catch (error) {
            console.error('Failed to load tag suggestions', error);
        }
    }
}
